/**
 * File components/SelectionSummary.js
 *
 * To display the booking choices made and calculate an approximative price
 *
 * Learn more: https://saint-augustin.ch
 */
import React from 'react';
import { useBookingDataContext } from '../context/BookingDataProvider';

// Import style sheets
import '../styles/SelectionSummary.css'

const SelectionSummary = () => {
	const { 
		locations, 
		nbPersons, 
		beginBooking, 
		endBooking
	} = useBookingDataContext();

	const formatDateTime = (dateString) => {
		if (!dateString) return '';
		const date = new Date(dateString);
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0'); // Mois commence à 0
		const year = date.getFullYear();
		const hours = String(date.getHours()).padStart(2, '0');
		const minutes = String(date.getMinutes()).padStart(2, '0');

		return `${day}.${month}.${year}, ${hours}:${minutes}`;
	};

	const calcLocationPrice = (location) => {
		var calcPrice = 0;
		const bookingStart = new Date(beginBooking);
		const bookingEnd = new Date(endBooking);

		// 1. Filtrer les tarifs "amount" qui couvrent TOUTE la réservation
		const applicableAmounts = location.tariffs.filter(tariff => tariff.price_type === 'amount' 
			&& bookingCoversTariff(bookingStart, bookingEnd, tariff)
		);

		// 2. Choisir le premier ou le moins cher parmi eux
		const selectedAmountTariff = applicableAmounts.sort((a, b) => parseFloat(a.price) - parseFloat(b.price))[0] || null;

		// 3. Filtrer les pourcentages applicables
		const applicablePercentages = location.tariffs.filter(tariff => tariff.price_type === 'percentage'
			&& bookingCoversTariff(bookingStart, bookingEnd, tariff)
		);

		// 4. Calculer le pourcentage de majoration qui s'applique
		const maxPercentage = applicablePercentages.length > 0
        	? Math.max(...applicablePercentages.map(t => parseFloat(t.price)))
        	: 0;

		// 5. Retourner le tarif choisi + éventuel pourcentage
		if (selectedAmountTariff && selectedAmountTariff.price != null) {
			calcPrice = parseFloat(selectedAmountTariff.price) + parseFloat(selectedAmountTariff.price)*maxPercentage / 100;
		} else {
			calcPrice = 0;
		}

		return calcPrice;
	};

	function bookingCoversTariff(start, end, tariff) {
		let current = new Date(start);

		while (current <= end) {
			const dayName = current.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
			const dayOk = tariff.available_days[dayName] === 1;

			const timeStr = current.toTimeString().slice(0,5); // "HH:mm"
			const periodOk = tariff.available_periods.some(([from, to]) => 
				timeStr >= from && timeStr <= to
			);

			if (!dayOk || !periodOk) {
				return false; // si un seul créneau ne correspond pas, on élimine le tarif
			}

			current.setMinutes(current.getMinutes() + 30); // avancer par pas de 30 min
		}
		return true;
	}
	
	var grandTotal = 0;
	var depositTotal = 0;
	var locationPrice = 0;
	const selectedData = locations
		.filter(loc => loc.isSelected)
		.map(loc => {
			locationPrice = calcLocationPrice(loc);
			grandTotal += locationPrice;
			depositTotal += parseFloat(loc.deposit);
			const selectedEquipments = (loc.equipments || []).filter(eq => eq.isSelected)
			.map(eq => {
				var calcPrice = parseFloat(eq.price || 0);
				if (eq.price_type === 'pro_hour') 
					calcPrice = calcPrice * eq.hours_per_price;
				else if (eq.price_type === 'pro_person') 
					calcPrice = calcPrice * Math.ceil((nbPersons-1) / eq.persons_per_price);
				grandTotal += calcPrice;
				return {
					name: eq.name,
					price_type: eq.price_type,
					hours_per_price: eq.hours_per_price,
					persons_per_price: eq.persons_per_price,
					price: eq. price,
					calculatedPrice: calcPrice
				}
			});
			return {
				title: loc.title.rendered,
				building: loc.building_name,
				equipments: selectedEquipments,
				calculatedPrice: locationPrice
			};
		});

	return (<>
		<div className="selection-summary card-body">
			
			<label htmlFor="beginBooking"><strong>Dates&nbsp;:</strong> du&nbsp;{formatDateTime(beginBooking)} au&nbsp;{formatDateTime(endBooking)}</label><br/><br/>
			<label htmlFor="nbPersons"><strong>Nombre de personnes&nbsp;:</strong> {nbPersons}</label><br/><br/>
			
			{selectedData.map((loc, idx) => (
				<div key={idx}>
					<label><strong>{loc.title} – {loc.building}:</strong></label> <span className="equipment-price">CHF {loc.calculatedPrice.toFixed(2)}</span>
					<ul>
						{loc.equipments.map((eq, i) => {
							const suffix =
								eq.price_type === 'pro_person'
									? '/'+eq.persons_per_price+'personne(s)'
									: eq.price_type === 'pro_hour'
									? '/'+eq.persons_per_price+'heure(s)'
									: '';
							const showCalculated = eq.price_type === 'pro_person' || eq.price_type === 'pro_hour';
							return (
								<li key={i}>
									{eq.name} <span className="equipment-price"> CHF {parseFloat(eq.calculatedPrice).toFixed(2)}</span>
								</li>
							);
						})}
					</ul>
				</div>
			))}
			<hr />
			<h3>TOTAL: <span className="equipment-price">CHF {grandTotal.toFixed(2)}</span></h3>
			<h4>Et une caution: <span className="equipment-price">CHF {depositTotal.toFixed(2)}</span></h4>
						
		</div>
		
	</>);
};

export default SelectionSummary;